import { supabase } from '../lib/supabase';

// Hjelpefunksjon for å opprette notifikasjon
async function createNotification(notificationData: {
  residence_id: number;
  notification_type: 'like' | 'comment' | 'event_change' | 'new_user' | 'new_post';
  entity_id?: string;
  entity_table?: string;
  title: string;
  metadata?: any;
}) {
  try {
    // Bruk en database function istedenfor direkte insert for å omgå RLS
    const { error } = await supabase.rpc('create_residence_notification', {
      p_residence_id: notificationData.residence_id,
      p_notification_type: notificationData.notification_type,
      p_title: notificationData.title,
      p_entity_id: notificationData.entity_id || undefined,
      p_entity_table: notificationData.entity_table || undefined,
      p_metadata: notificationData.metadata || {}
    });

    if (error) {
      console.error('Feil ved opprettelse av notifikasjon:', error);
    }
  } catch (err) {
    console.error('Exception ved opprettelse av notifikasjon:', err);
  }
}

// 1. Likes på styrets innlegg
export async function notifyLikeOnBoardPost(postId: number, residenceId: number, likerName: string, postTitle: string) {
  await createNotification({
    residence_id: residenceId,
    notification_type: 'like',
    entity_id: postId.toString(),
    entity_table: 'base_post',
    title: `${likerName} likte "${postTitle}"`,
    metadata: {
      post_id: postId,
      liker_name: likerName,
      post_title: postTitle
    }
  });
}

// 2. Kommentarer på styrets innlegg
export async function notifyCommentOnBoardPost(postId: number, residenceId: number, commenterName: string, postTitle: string, commentText: string) {
  await createNotification({
    residence_id: residenceId,
    notification_type: 'comment',
    entity_id: postId.toString(),
    entity_table: 'base_post',
    title: `${commenterName} kommenterte "${postTitle}"`,
    metadata: {
      post_id: postId,
      commenter_name: commenterName,
      post_title: postTitle,
      comment_text: commentText.substring(0, 100) // Begrens lengden
    }
  });
}

// 3. Event-deltakelse endringer
export async function notifyEventAttendanceChange(postId: number, residenceId: number, userName: string, eventTitle: string, newStatus: 'attending' | 'declined', previousStatus?: string, explanation?: string) {
  const statusText = newStatus === 'attending' ? 'kommer' : 'kommer ikke';
  const previousStatusText = previousStatus === 'attending' ? 'kom' : previousStatus === 'declined' ? 'kom ikke' : 'ikke svart';
  
  let title = previousStatus 
    ? `${userName} endret svar på "${eventTitle}" fra ${previousStatusText} til ${statusText}`
    : `${userName} svarte ${statusText} på "${eventTitle}"`;

  // Legg til begrunnelse i tittelen hvis brukeren avslo med begrunnelse
  if (newStatus === 'declined' && explanation) {
    title += ` (Begrunnelse: ${explanation})`;
  }

  await createNotification({
    residence_id: residenceId,
    notification_type: 'event_change',
    entity_id: postId.toString(),
    entity_table: 'base_post',
    title,
    metadata: {
      post_id: postId,
      user_name: userName,
      event_title: eventTitle,
      new_status: newStatus,
      previous_status: previousStatus,
      explanation: explanation || null
    }
  });
}

// Hjelpefunksjon for å få dynamisk navn på bofellesskap basert på type
function getResidenceTypeLabel(type: 'condo' | 'cooperative' | null | undefined): string {
  if (type === 'condo') return 'Sameiet';
  if (type === 'cooperative') return 'Borettslaget';
  return 'Bofellesskapet';
}

// 4. Nye beboere
export async function notifyNewUser(residenceId: number, userName: string) {
  // Hent bofellesskapets type for å bruke riktig navn
  let residenceTypeLabel = 'Bofellesskapet'; // Standard fallback
  
  try {
    const { data: residence, error } = await supabase
      .from('residence')
      .select('type')
      .eq('id', residenceId)
      .single();
    
    if (!error && residence) {
      residenceTypeLabel = getResidenceTypeLabel(residence.type as 'condo' | 'cooperative' | null);
    }
  } catch (err) {
    console.error('Feil ved henting av bofellesskapets type:', err);
    // Bruk standard fallback
  }
  
  await createNotification({
    residence_id: residenceId,
    notification_type: 'new_user',
    entity_id: residenceId.toString(),
    entity_table: 'user_belong',
    title: `${userName} ble medlem av ${residenceTypeLabel.toLowerCase()}`,
    metadata: {
      user_name: userName,
      residence_id: residenceId
    }
  });
}

// 5. Nye forum-innlegg
export async function notifyNewPost(postId: number, residenceId: number, authorName: string, postTitle: string, forumType: number) {
  const forumTypeText = forumType === 1 ? 'discussion' : forumType === 2 ? 'event' : 'post';
  
  await createNotification({
    residence_id: residenceId,
    notification_type: 'new_post',
    entity_id: postId.toString(),
    entity_table: 'base_post',
    title: `${authorName} opprettet ny ${forumTypeText}: "${postTitle}"`,
    metadata: {
      post_id: postId,
      author_name: authorName,
      post_title: postTitle,
      forum_type: forumType
    }
  });
}

// Hjelpefunksjon for å sjekke om et innlegg tilhører styret
export async function isBoardPost(postId: number): Promise<{ isBoard: boolean; residenceId?: number; title?: string }> {
  try {
    console.log('🔍 isBoardPost kalt for postId:', postId);
    
    const { data, error } = await supabase
      .from('base_post')
      .select('residence_id, title, user_id, board_initiated')
      .eq('id', postId)
      .single();

    if (error || !data) {
      console.log('❌ Feil ved henting av post data:', error);
      return { isBoard: false };
    }

    console.log('📋 Post data:', {
      residence_id: data.residence_id,
      title: data.title || 'null',
      user_id: data.user_id || 'null',
      board_initiated: data.board_initiated
    });

    // Sjekk om innlegget er markert som styre-initiert
    if (data.board_initiated) {
      console.log('✅ Post er markert som board_initiated = true');
      return {
        isBoard: true,
        residenceId: data.residence_id,
        title: data.title || undefined
      };
    }

    console.log('🔍 Post er ikke board_initiated, sjekker om bruker er styremedlem...');

    // Sjekk om brukeren som opprettet innlegget er styremedlem i det boretslaget
    const { data: userBelong, error: userError } = await supabase
      .from('user_belong')
      .select('role')
      .eq('residence_id', data.residence_id)
      .eq('profiles_id', data.user_id || '')
      .eq('role', 'board')
      .single();

    console.log('👤 User belong data:', userBelong, 'error:', userError);

    // Hvis brukeren er styremedlem, er det et styreinnlegg
    if (!userError && userBelong) {
      console.log('✅ Bruker er styremedlem');
      return {
        isBoard: true,
        residenceId: data.residence_id,
        title: data.title || undefined
      };
    }

    console.log('❌ Post tilhører ikke styret');
    return {
      isBoard: false,
      residenceId: data.residence_id,
      title: data.title || undefined
    };
  } catch (err) {
    console.error('❌ Feil ved sjekking av styreinnlegg:', err);
    return { isBoard: false };
  }
}

// Hjelpefunksjon for å sjekke om et event tilhører styret
export async function isBoardEvent(postId: number): Promise<{ isBoard: boolean; residenceId?: number; title?: string }> {
  try {
    // Sjekk om base_post har board_initiated = true
    const { data, error } = await supabase
      .from('base_post')
      .select('residence_id, title, board_initiated')
      .eq('id', postId)
      .single();

    if (error || !data) {
      return { isBoard: false };
    }

    if (data.board_initiated) {
      return {
        isBoard: true,
        residenceId: data.residence_id,
        title: data.title || undefined
      };
    }

    return { isBoard: false };
  } catch (err) {
    console.error('Feil ved sjekking av styreevent:', err);
    return { isBoard: false };
  }
}
